import React from "react";
import Link from "next/link";
import Image from "next/image";
import BlogCategory from "../blogCategory";
import { ProjectApi } from "@/types";
import clsx from "clsx";
import Box from "@sendsprint/ui-react/components/box";
import Typography from "@sendsprint/ui-react/components/typography";
import { resolveBlogDetails } from "../../utils/resolveBlogDetails";
import { formatDate } from "@/utils/formatDate";

interface Props extends ProjectApi.Blog.BlogData {
  className?: string;
}

const BlogCard = ({ className, ...blog }: Props) => {
  const { date, url, imageUrl, altText, title, category, author, authorUrl } =
    blog;

  const blogDate = formatDate(date);

  const resolvedBlogDetails = resolveBlogDetails(blog);

  return (
    <Link
      href={url}
      data-testid="blog-card"
      className={clsx(
        "ss:shadow-lg ss:group ss:overflow-hidden ss:rounded-xl",
        className,
      )}
    >
      <Box className="ss:flex ss:flex-col ss:h-full">
        <Box className="ss:relative ss:after:pb-[100%] ss:after:block ss:rounded-t-base ss:group-hover:ss:bg-primary1-500 ss:w-full ss:bg-neutral-400 ss:overflow-hidden ss:bg-cover ss:bg-no-repeat ss:max-w-full">
          <Box className="ss:w-full ss:h-full ss:absolute">
            <Image
              src={imageUrl}
              alt={altText || title}
              className="ss:w-full ss:object-cover ss:h-full"
              blurDataURL={imageUrl}
              width={300}
              height={300}
            />
          </Box>
        </Box>
        <Box className="ss:bg-neutral-100 ss:duration-200 ss:group-hover:bg-primary1-500 ss:group-hover:shadow-2xl ss:grow ss:text-center ss:rounded-b-base ss:px-4 ss:py-6">
          <Box className="ss:flex ss:items-center ss:gap-3 ss:flex-wrap">
            {category.map((item, index) => (
              <BlogCategory category={item} key={index} />
            ))}
          </Box>
          <Typography
            as="h3"
            variant="h6"
            className="ss:text-neutral-500 ss:font-semibold ss:duration-200 ss:group-hover:text-neutral-50 ss:text-left ss:break-all ss:h-28"
          >
            {resolvedBlogDetails.title}
          </Typography>
          <Box className="ss:flex ss:mt-8 ss:justify-start ss:items-center ss:gap-4">
            <Box>
              <Image
                alt={`${resolvedBlogDetails.authorText} picture`}
                src={resolvedBlogDetails.authorUrl}
                className="ss:h-10 ss:w-10 ss:rounded-full"
                width={40}
                height={40}
              />
            </Box>
            <Box className="ss:flex ss:flex-col ss:items-start ss:gap-1">
              <Typography
                as="p"
                variant="paragraphSmall"
                className="ss:group-hover:text-neutral-50 ss:duration-200 ss:font-bold"
              >
                {resolvedBlogDetails.authorText}
              </Typography>
              <Typography
                as="p"
                variant="paragraphVerySmall"
                className="ss:text-neutral-500 ss:group-hover:text-neutral-100"
              >
                {blogDate}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Link>
  );
};

export default BlogCard;
